/**
 * GovLGPD — Central de LGPD com direitos do titular e auditoria.
 *
 * v0.99.9 · Wave 3 · Fev/2026 — módulo GOV puro para atender aos
 * requisitos da Lei Geral de Proteção de Dados (Lei 13.709/2018).
 */
import { ShieldCheck, FileText, Trash, Eye, PencilSimple, Envelope, Info } from "@phosphor-icons/react";

const RIGHTS = [
  { icon: Eye, title: "Acesso aos dados", desc: "Cidadão pode consultar quais dados o órgão mantém sobre ele." },
  { icon: PencilSimple, title: "Correção", desc: "Corrigir dados incompletos, inexatos ou desatualizados." },
  { icon: Trash, title: "Apagamento", desc: "Solicitar exclusão de dados tratados com base em consentimento." },
  { icon: FileText, title: "Portabilidade", desc: "Exportar dados em formato estruturado (JSON/CSV)." },
  { icon: Info, title: "Informação", desc: "Saber com quem os dados são compartilhados." },
  { icon: Envelope, title: "Contato do Encarregado (DPO)", desc: "Falar diretamente com o Encarregado de Dados." },
];

export default function GovLGPD() {
  return (
    <div data-testid="gov-lgpd-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">Configurações</div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <ShieldCheck size={22} className="text-emerald-700" />
          LGPD & Compliance
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Central de conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018).
        </p>
      </div>

      <section className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-transparent p-5 mb-4">
        <div className="flex items-start gap-3">
          <ShieldCheck size={26} className="text-emerald-700 shrink-0" weight="fill" />
          <div>
            <div className="font-heading font-semibold text-slate-900 text-[15px]">Órgão em conformidade LGPD</div>
            <div className="text-[12px] text-slate-700 leading-relaxed mt-1">
              O AgentUS opera como <strong>operador de dados</strong> — o órgão é o <strong>controlador</strong>.
              Todos os dados dos cidadãos permanecem sob domínio do ente público, com criptografia em trânsito e em repouso,
              logs de acesso auditáveis e políticas de retenção configuráveis.
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-3 md:grid-cols-2">
        {RIGHTS.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.title} className="rounded-lg border border-slate-200 bg-white p-4 flex gap-3 items-start">
              <div className="w-9 h-9 rounded-md bg-emerald-50 border border-emerald-200 grid place-items-center shrink-0">
                <Icon size={16} className="text-emerald-700" weight="regular" />
              </div>
              <div>
                <div className="font-medium text-slate-900 text-[13px]">{r.title}</div>
                <div className="text-[11.5px] text-slate-500 leading-relaxed mt-0.5">{r.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 text-[12px] text-slate-700 leading-relaxed">
        <strong>Dados técnicos:</strong> criptografia AES-256 em repouso · TLS 1.3 em trânsito · hospedagem em nuvem soberana ·
        política de retenção: 5 anos (dados operacionais) / 20 anos (dados finalísticos, conforme legislação de arquivo público) ·
        auditoria completa acessível ao Encarregado de Dados (DPO) do órgão.
      </div>
    </div>
  );
}
